import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { manifestoChapters } from "@/components/landing/data";

export default function Manifesto() {
  const railRef = useRef(null);
  const dragState = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActive = () => {
    const rail = railRef.current;
    if (!rail) return;
    const railCenter = rail.getBoundingClientRect().left + rail.clientWidth / 2;
    const closest = Array.from(rail.querySelectorAll("article")).reduce((best, card, index) => {
      const rect = card.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - railCenter);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Infinity });
    setActiveIndex(closest.index);
  };

  const scrollToChapter = (index) => {
    const rail = railRef.current;
    if (!rail) return;
    const nextIndex = Math.min(manifestoChapters.length - 1, Math.max(0, index));
    const card = rail.querySelectorAll("article")[nextIndex];
    const left = card.offsetLeft - (rail.clientWidth - card.clientWidth) / 2;
    rail.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  };

  const startDrag = (event) => {
    if (event.pointerType !== "mouse") return;
    dragState.current = { active: true, startX: event.clientX, scrollLeft: railRef.current.scrollLeft };
    railRef.current.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event) => {
    if (!dragState.current.active) return;
    railRef.current.scrollLeft = dragState.current.scrollLeft - (event.clientX - dragState.current.startX);
  };

  const endDrag = () => {
    dragState.current.active = false;
  };

  return (
    <section id="manifesto" data-testid="manifesto-section" className="section-shell py-24 sm:py-32 lg:py-40">
      <div className="mb-14 grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
        <p data-testid="manifesto-eyebrow" className="eyebrow">Manifesto / chapters</p>
        <div>
          <h2 data-testid="manifesto-heading" className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-[-0.05em] text-white sm:text-6xl">
            The rules behind the work.
          </h2>
          <p data-testid="manifesto-swipe-hint" className="mt-5 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Swipe or drag through the chapters</p>
        </div>
      </div>

      <div className="relative">
        <div
          ref={railRef}
          data-testid="manifesto-steps-rail"
          data-lenis-prevent
          onScroll={updateActive}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="manifesto-rail flex cursor-grab select-none snap-x snap-mandatory gap-5 overflow-x-auto pb-8 active:cursor-grabbing"
        >
          {manifestoChapters.map(([number, title, text], index) => (
            <motion.article
              key={number}
              data-testid={`manifesto-chapter-${number}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              animate={{ scale: activeIndex === index ? 1 : 0.95, opacity: activeIndex === index ? 1 : 0.48 }}
              transition={{ duration: 0.55, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative min-h-[24rem] w-[82vw] min-w-[82vw] snap-center overflow-hidden rounded-[1.7rem] border p-8 shadow-2xl shadow-black/20 transition-[border-color,background-color] duration-500 sm:w-[28rem] sm:min-w-[28rem] sm:p-10 ${activeIndex === index ? "border-cyan-300/45 bg-[#11111B]" : "border-white/10 bg-[#0F0F17]/80"}`}
            >
              <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-300/10 blur-3xl transition-opacity group-hover:opacity-100" />
              <span className="font-mono text-sm text-cyan-300">[{number}]</span>
              <h3 className="mt-16 font-display text-2xl font-bold uppercase tracking-[-0.03em] text-white sm:text-3xl">{title}</h3>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">{text}</p>
            </motion.article>
          ))}
        </div>

        <div className="mt-2 flex items-center justify-between gap-6">
          <div data-testid="manifesto-progress" className="flex items-center gap-2">
            {manifestoChapters.map(([number], index) => (
              <button
                key={number}
                data-testid={`manifesto-dot-${number}`}
                aria-label={`Go to manifesto chapter ${number}`}
                onClick={() => scrollToChapter(index)}
                className={`h-2 rounded-full transition-[width,background-color] duration-500 ${activeIndex === index ? "w-10 bg-cyan-300 shadow-[0_0_16px_rgba(0,240,255,0.45)]" : "w-2 bg-white/15 hover:bg-white/30"}`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            <button data-testid="manifesto-prev-button" aria-label="Previous manifesto chapter" onClick={() => scrollToChapter(activeIndex - 1)} className="outline-button h-11 w-11 rounded-full"><ArrowLeft className="h-4 w-4" /></button>
            <button data-testid="manifesto-next-button" aria-label="Next manifesto chapter" onClick={() => scrollToChapter(activeIndex + 1)} className="cyan-button h-11 w-11 rounded-full"><ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </section>
  );
}
